import { authClient } from "lib/auth-client";
import {
  assertAuthAttemptCurrent,
  authAttemptFetchOptions,
  beginAuthAttempt,
  type AuthAttempt,
} from "lib/auth-attempt";
import { buildUserInfoFromBetterAuthSession } from "lib/complete-better-auth-login";
import { notifyBetterAuthTwoFactorRedirect } from "lib/better-auth-two-factor-bridge";
import { BETTER_AUTH_MFA_EMAIL_HEADER } from "config/better-auth.constants";
import { store } from "store/actions/store";
import type { AppState } from "store/reducers/Reducer";
import type {
  LoginDataType,
  MfaSetupType,
  RegisterDataType,
  UserInfoDataType,
} from "store/types/auth";
import { ServerError } from "utils/genericUtilityFunctions";

type BetterAuthSignInEmailResult = {
  twoFactorRequired: boolean;
  userInfo?: UserInfoDataType;
};

type BetterAuthClientError = {
  status?: number;
  message?: string;
  code?: unknown;
  data?: unknown;
  retry_after?: string | number;
};

/**
 * Carry the backend's `code` and `data` into the app error. Callers branch on
 * them (SSO required, lockout, deactivated, pending deletion); dropping them
 * turned every one of those into a generic toast.
 */
export function toBetterAuthServerError(
  error: BetterAuthClientError,
  fallbackMessage: string
): ServerError {
  const data =
    error.data && typeof error.data === "object" && !Array.isArray(error.data)
      ? (error.data as Record<string, unknown>)
      : undefined;
  return new ServerError(
    error.status ?? 400,
    error.message ?? fallbackMessage,
    undefined,
    typeof error.code === "string" ? error.code : undefined,
    data,
    error.retry_after
  );
}

function normalizeAuthEmail(email: string): string {
  return email.trim().toLowerCase();
}

function isTwoFactorRedirectResponse(data: unknown): boolean {
  return (
    typeof data === "object" &&
    data !== null &&
    "twoFactorRedirect" in data &&
    (data as { twoFactorRedirect?: boolean }).twoFactorRedirect === true
  );
}

export async function signInWithBetterAuthEmail(
  loginData: LoginDataType,
  turnstileHeaders?: Record<string, string>,
  attempt: AuthAttempt = beginAuthAttempt()
): Promise<BetterAuthSignInEmailResult> {
  const result = await authClient.signIn.email(
    {
      email: normalizeAuthEmail(loginData.email),
      password: loginData.password,
      rememberMe: loginData.remember_me ?? true,
    },
    {
      ...authAttemptFetchOptions(attempt),
      headers: turnstileHeaders,
    }
  );
  assertAuthAttemptCurrent(attempt);

  if (result.error) {
    throw toBetterAuthServerError(result.error, "Sign in failed");
  }

  if (isTwoFactorRedirectResponse(result.data)) {
    notifyBetterAuthTwoFactorRedirect();
    return { twoFactorRequired: true };
  }

  const userInfo = await buildUserInfoFromBetterAuthSession(attempt.session, attempt);
  return { twoFactorRequired: false, userInfo };
}

function readPendingMfaLoginEmail(): string | null {
  const email = (store.getState() as AppState).auth.email?.trim().toLowerCase();
  return email && email.length > 0 ? email : null;
}

function resolveMfaLoginEmail(payload: MfaSetupType): string | null {
  const fromPayload = payload.email?.trim().toLowerCase();
  if (fromPayload) {
    return fromPayload;
  }
  return readPendingMfaLoginEmail();
}

function buildMfaLoginVerifyHeaders(email: string | null): Record<string, string> | undefined {
  if (!email) {
    return undefined;
  }

  return {
    [BETTER_AUTH_MFA_EMAIL_HEADER]: email,
  };
}

function resolveMfaVerifyErrorCode(error: {
  status?: number;
  message?: string | null;
  code?: string | null;
}): string | undefined {
  const message = `${error.code ?? ""} ${error.message ?? ""}`.toLowerCase();
  if (
    message.includes("invalid_two_factor_cookie") ||
    message.includes("invalid two factor cookie")
  ) {
    return "SESSION_EXPIRED";
  }
  if (error.status === 401 || error.status === 400) {
    return "INVALID_TOTP_TOKEN";
  }
  return undefined;
}

export async function completeBetterAuthMfaLogin(
  payload: MfaSetupType,
  attempt: AuthAttempt = beginAuthAttempt()
): Promise<UserInfoDataType> {
  const email = resolveMfaLoginEmail(payload);
  const result = await authClient.twoFactor.verifyTotp(
    {
      code: payload.token,
    },
    {
      ...authAttemptFetchOptions(attempt),
      headers: buildMfaLoginVerifyHeaders(email),
    }
  );
  assertAuthAttemptCurrent(attempt);

  if (result.error) {
    throw new ServerError(
      result.error.status ?? 400,
      result.error.message ?? "Invalid code",
      undefined,
      resolveMfaVerifyErrorCode(result.error)
    );
  }

  return buildUserInfoFromBetterAuthSession(attempt.session, attempt);
}

export async function requestBetterAuthPasswordReset(
  email: string,
  turnstileHeaders?: Record<string, string>
): Promise<void> {
  const frontendOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const result = await authClient.requestPasswordReset(
    {
      email: normalizeAuthEmail(email),
      redirectTo: `${frontendOrigin}/reset_password`,
    },
    {
      headers: turnstileHeaders,
    }
  );

  if (result.error) {
    throw toBetterAuthServerError(result.error, "Request failed");
  }
}

export async function signUpWithBetterAuthEmail(
  registerData: RegisterDataType,
  turnstileHeaders?: Record<string, string>,
  attempt: AuthAttempt = beginAuthAttempt()
): Promise<UserInfoDataType> {
  const result = await authClient.signUp.email(
    {
      email: normalizeAuthEmail(registerData.email),
      password: registerData.password,
      name: registerData.email.split("@")[0] || "",
    },
    {
      ...authAttemptFetchOptions(attempt),
      headers: turnstileHeaders,
    }
  );
  assertAuthAttemptCurrent(attempt);

  if (result.error) {
    throw toBetterAuthServerError(result.error, "Sign up failed");
  }

  return buildUserInfoFromBetterAuthSession(attempt.session, attempt);
}

export async function resetBetterAuthPassword(
  token: string,
  newPassword: string,
  turnstileHeaders?: Record<string, string>
): Promise<void> {
  const result = await authClient.resetPassword(
    {
      newPassword,
      token,
    },
    {
      headers: turnstileHeaders,
    }
  );

  if (result.error) {
    throw toBetterAuthServerError(result.error, "Reset failed");
  }
}
